import React, { useState, useEffect, useRef } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import ChangelogService from '../../service/ChangelogService';
import './ChangelogSidebar.css';

export const ChangelogSidebar = ({ visible, onHide, customers }) => {
  const changelogService = new ChangelogService();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allEntries, setAllEntries] = useState([]); // Store all fetched entries
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [sortField, setSortField] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState(-1); // -1 = descending (newest first)
  
  // Use ref to avoid stale closure issues
  const nextTokenRef = useRef(null);
  
  useEffect(() => {
    nextTokenRef.current = nextToken;
  }, [nextToken]);

  // Defensively parse a changelog value that may be a JSON string, a
  // double-encoded JSON string (legacy backend bug), or already an object.
  // Returns an object/array, or null if it cannot be turned into one — which
  // prevents the details view from spreading a raw string character-by-character.
  const parseValues = (raw) => {
    let v = raw;
    for (let i = 0; i < 3 && typeof v === 'string'; i++) {
      try {
        v = JSON.parse(v);
      } catch {
        break;
      }
    }
    return v && typeof v === 'object' ? v : null;
  };

  // Parse JSON strings in changelog entries
  const parseChangelogEntry = (entry) => ({
    ...entry,
    oldValues: parseValues(entry.oldValues),
    newValues: parseValues(entry.newValues),
  });

  // Load changelog entries - simple version without filters
  const loadChangelog = async (append = false) => {
    setLoading(true);
    try {
      const token = append ? nextTokenRef.current : null;
      
      const result = await changelogService.listChangelog({
        limit: rows,
        nextToken: token
      });
      
      const rawEntries = result.data?.listChangelog?.entries || [];
      const newEntries = rawEntries.map(parseChangelogEntry);
      
      if (append) {
        setAllEntries(prev => [...prev, ...newEntries]);
      } else {
        setAllEntries(newEntries);
      }
      
      setNextToken(result.data?.listChangelog?.nextToken);
      setHasMore(!!result.data?.listChangelog?.nextToken);
    } catch (error) {
      console.error('Error loading changelog:', error);
      setAllEntries([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort change
  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
    setFirst(0); // Reset to first page on sort change
  };

  // Handle page change - load more data if needed
  const onPage = async (event) => {
    const newFirst = event.first;
    const newRows = event.rows;
    
    setFirst(newFirst);
    setRows(newRows);
    
    // Check if we need to load more data
    const currentPage = Math.floor(newFirst / newRows);
    const totalPages = Math.ceil(allEntries.length / newRows);
    
    // If we're on the last page and there's more data, fetch next batch
    if (currentPage >= totalPages - 1 && hasMore && !loading) {
      await loadChangelog(true); // Append mode
    }
  };

  // Update displayed entries based on pagination and sorting
  useEffect(() => {
    // Sort all entries client-side
    const sorted = [...allEntries].sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      
      if (valA == null && valB == null) return 0;
      if (valA == null) return sortOrder;
      if (valB == null) return -sortOrder;
      
      let result;
      if (typeof valA === 'string') {
        result = valA.localeCompare(valB);
      } else {
        result = valA < valB ? -1 : valA > valB ? 1 : 0;
      }
      return result * sortOrder;
    });

    const start = first;
    const end = first + rows;
    const displayedEntries = sorted.slice(start, end);
    setEntries(displayedEntries);
    
    // Set totalRecords to a large number if we have more data, otherwise use actual count
    const total = hasMore ? allEntries.length + 1000 : allEntries.length;
    setTotalRecords(total);
  }, [allEntries, first, rows, hasMore, nextToken, sortField, sortOrder]);

  useEffect(() => {
    if (visible) {
      // Reset state and load data when sidebar opens
      setAllEntries([]);
      setEntries([]);
      setNextToken(null);
      setHasMore(true);
      setFirst(0);
      loadChangelog(false);
    }
  }, [visible]);

  // Template functions
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="null-value">—</span>;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : <span className="null-value">—</span>;
    }
    if (typeof value === 'object') {
      // For nested objects, show a formatted version
      return (
        <div className="nested-object">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="nested-field">
              <span className="nested-label">{k}:</span> {String(v)}
            </div>
          ))}
        </div>
      );
    }
    return String(value);
  };

  const timestampTemplate = (rowData) => {
    const date = new Date(rowData.timestamp);
    return date.toLocaleString();
  };

  const operationTemplate = (rowData) => {
    const severityMap = {
      CREATE: 'success',
      UPDATE: 'info',
      DELETE: 'danger'
    };
    return <Tag value={rowData.operation} severity={severityMap[rowData.operation]} />;
  };

  const userTemplate = (rowData) => {
    return <span className="changelog-user">{rowData.userName || rowData.userId}</span>;
  };

  const customerTemplate = (rowData) => {
    const customer = customers.find(c => c.customerId === rowData.recordId);
    return customer ? `${customer.customerNr} - ${customer.surName}` : rowData.recordId;
  };

  const actionsTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-sm"
        onClick={() => {
          setSelectedEntry(rowData);
          setDetailsVisible(true);
        }}
        tooltip="View Details"
      />
    );
  };

  const header = (
    <div className="changelog-header">
      <h3>Change History</h3>
      <div className="changelog-filters">
        <Button
          icon="pi pi-refresh"
          className="p-button-text"
          onClick={() => loadChangelog(false)}
          tooltip="Refresh"
        />
      </div>
    </div>
  );

  return (
    <>
      <Sidebar
        visible={visible}
        position="right"
        onHide={onHide}
        className="changelog-sidebar"
        style={{ width: '60vw' }}
      >
        <DataTable
          value={entries}
          loading={loading}
          header={header}
          paginator
          lazy
          rows={rows}
          first={first}
          totalRecords={totalRecords}
          onPage={onPage}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="No changes found"
          responsiveLayout="scroll"
        >
          <Column field="timestamp" header="Time" body={timestampTemplate} sortable style={{ width: '180px' }} />
          <Column field="operation" header="Action" body={operationTemplate} sortable style={{ width: '100px' }} />
          <Column field="userId" header="User" body={userTemplate} sortable />
          <Column field="recordId" header="Customer" body={customerTemplate} sortable />
          <Column body={actionsTemplate} style={{ width: '80px' }} />
        </DataTable>
      </Sidebar>

      <Dialog
        visible={detailsVisible}
        onHide={() => setDetailsVisible(false)}
        header="Change Details"
        style={{ width: '60vw' }}
        modal
      >
        {selectedEntry && (
          <div className="changelog-details">
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <div><strong>Time:</strong> {new Date(selectedEntry.timestamp).toLocaleString()}</div>
              <div><strong>Operation:</strong> <Tag value={selectedEntry.operation} /></div>
              <div><strong>User:</strong> {selectedEntry.userName || selectedEntry.userId}</div>
              <div><strong>Customer ID:</strong> {selectedEntry.recordId}</div>
            </div>
            
            {selectedEntry.oldValues && selectedEntry.newValues && (() => {
              const allKeys = [...new Set([
                ...Object.keys(selectedEntry.oldValues || {}),
                ...Object.keys(selectedEntry.newValues || {})
              ])];
              
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #dee2e6', width: '20%' }}>Field</th>
                      <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #dee2e6', width: '40%' }}>Previous</th>
                      <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #dee2e6', width: '40%' }}>New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allKeys.map(key => {
                      const oldVal = selectedEntry.oldValues?.[key];
                      const newVal = selectedEntry.newValues?.[key];
                      const oldStr = JSON.stringify(oldVal);
                      const newStr = JSON.stringify(newVal);
                      const changed = oldStr !== newStr;
                      
                      return (
                        <tr key={key} style={{ backgroundColor: changed ? '#fff8e1' : 'transparent' }}>
                          <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid #eee', fontWeight: 600 }}>{key}</td>
                          <td style={{
                            padding: '0.4rem 0.5rem',
                            borderBottom: '1px solid #eee',
                            color: changed ? '#c63737' : '#666',
                            textDecoration: changed ? 'line-through' : 'none',
                          }}>
                            {formatValue(oldVal)}
                          </td>
                          <td style={{
                            padding: '0.4rem 0.5rem',
                            borderBottom: '1px solid #eee',
                            color: changed ? '#2e7d32' : '#666',
                            fontWeight: changed ? 600 : 400,
                          }}>
                            {formatValue(newVal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}

            {selectedEntry.operation === 'CREATE' && selectedEntry.newValues && !selectedEntry.oldValues && (
              <div className="detail-section">
                <h4>Created Values:</h4>
                <div className="detail-values">
                  {Object.entries(selectedEntry.newValues).map(([key, value]) => (
                    <div key={key} className="detail-field">
                      <span className="field-label">{key}:</span>
                      <span className="field-value" style={{ color: '#2e7d32', fontWeight: 600 }}>{formatValue(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedEntry.operation === 'DELETE' && selectedEntry.oldValues && !selectedEntry.newValues && (
              <div className="detail-section">
                <h4>Deleted Values:</h4>
                <div className="detail-values">
                  {Object.entries(selectedEntry.oldValues).map(([key, value]) => (
                    <div key={key} className="detail-field">
                      <span className="field-label">{key}:</span>
                      <span className="field-value" style={{ color: '#c63737', textDecoration: 'line-through' }}>{formatValue(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};
